import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Receipt } from 'lucide-react';

import { listerFrais, messageErreurFrais } from '../../lib/expensesApi';
import { formatDate, formatMois } from '../../lib/formats';
import { libelle, referenceLisible, texteSur } from '../../lib/libelles';
import { cn, formatCurrency } from '../../lib/utils';
import { Button } from '../ui/button';
import { EmptyState } from '../ui/empty-state';
import { LoadingState } from '../ui/loading-state';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/table';
import { BarrePagination } from '../ui/pagination-bar';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { usePaginationServeur } from '../../hooks/usePagination';
import { useDerniereDemande } from '../../hooks/useDerniereDemande';

/** Mois (AAAA-MM) d'un frais: la date du journal du serveur, à défaut la date de saisie. */
const moisDuFrais = (ligne) => String(ligne.journal_date || ligne.occurred_at || '').slice(0, 7);

/**
 * Tableau des frais, comme dans le classeur: une ligne par frais, les
 * montants alignés à droite, le total en bas (et le sous-total du mois quand
 * `parMois` est actif).
 *
 * Les lignes viennent du journal (`GET /finance/expenses`) : frais payés par
 * le chauffeur et frais payés par la caisse y sont ensemble, à ne pas
 * additionner avec les frais du trajet.
 *
 * La liste se lit par pages (20, 50 ou 100 frais), demandées au serveur; la
 * page vit dans l'adresse (`?frais-page=`). Le total du bas et le total de
 * chaque mois viennent du serveur et portent sur tous les frais du filtre,
 * jamais sur la page: un mois coupé entre deux pages garde son total entier.
 *
 * Props:
 * - `truckId`, `tripId`, `convoyId` : un seul filtre à la fois, en général;
 * - `refreshKey` : change pour recharger après une saisie;
 * - `onCancel(frais)` : si fourni, ajoute « Annuler » sur chaque ligne (le frais
 *   est extourné, jamais effacé);
 * - `compact` : une liste au lieu d'un tableau, pour une colonne étroite (le
 *   détail d'un convoi). Neuf colonnes ne tiennent pas dans 360 px: la liste
 *   garde les mêmes informations, une ligne par frais, sans défilement
 *   latéral.
 */
export default function ExpensesTable({
  truckId,
  tripId,
  convoyId,
  refreshKey = 0,
  parMois = false,
  onCancel,
  busy = false,
  onLoaded,
  compact = false,
}) {
  const [lignes, setLignes] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [total, setTotal] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  // Un autre camion, trajet ou convoi ramène à la première page.
  const pagination = usePaginationServeur(total, {
    cle: 'frais',
    reinitialiser: `${truckId || ''}|${tripId || ''}|${convoyId || ''}`,
  });
  const { page, limit: taille } = pagination;
  const contexte = `${truckId || ''}|${tripId || ''}|${convoyId || ''}`;
  const contexteLu = useRef(contexte);

  const nouvelleDemande = useDerniereDemande();
  const charger = useCallback(async () => {
    const perimee = nouvelleDemande();
    setChargement(true);
    setErreur('');
    // Un autre camion, trajet ou convoi: on ne garde pas les lignes de l'ancien à l'écran.
    if (contexteLu.current !== contexte) {
      contexteLu.current = contexte;
      setLignes([]);
      setTotal(0);
    }
    try {
      const resultat = await listerFrais({
        truck_id: truckId,
        trip_id: tripId,
        convoy_id: convoyId,
        page,
        page_size: taille,
      });
      if (perimee()) return;
      setLignes(resultat.items);
      setTotaux(resultat.totals);
      setTotal(resultat.total);
      if (onLoaded) onLoaded(resultat);
    } catch (error) {
      if (perimee()) return;
      setErreur(messageErreurFrais(error, 'Les frais n’ont pas pu être chargés.'));
    } finally {
      if (!perimee()) setChargement(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckId, tripId, convoyId, page, taille, nouvelleDemande]);

  useEffect(() => {
    charger();
  }, [charger, refreshKey]);
  useRafraichirSur(['depenses', 'finance', 'trajets', 'camions'], charger);

  const groupes = useMemo(() => {
    if (!parMois) return [{ cle: 'tous', titre: null, lignes }];
    const parCle = new Map();
    lignes.forEach((ligne) => {
      const cle = moisDuFrais(ligne) || 'sans-date';
      if (!parCle.has(cle)) parCle.set(cle, []);
      parCle.get(cle).push(ligne);
    });
    return [...parCle.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([cle, rows]) => ({
        cle,
        titre: cle === 'sans-date' ? 'Sans date' : formatMois(cle),
        lignes: rows,
      }));
  }, [lignes, parMois]);

  // Première lecture, ou page vide le temps de revenir à la dernière page existante.
  if ((chargement && lignes.length === 0) || (lignes.length === 0 && total > 0 && !erreur)) {
    return <LoadingState shape="table" />;
  }

  if (erreur) {
    return (
      <EmptyState variant="error" title="Les frais ne sont pas disponibles" description={erreur} actionLabel="Réessayer" onAction={charger} />
    );
  }

  if (lignes.length === 0) {
    return (
      <EmptyState
        variant="empty"
        icon={Receipt}
        title="Aucun frais enregistré"
        description={
          compact
            ? 'Les frais saisis apparaîtront ici.'
            : 'Les frais saisis apparaîtront ici, avec leur catégorie, leur date et la personne qui les a payés.'
        }
        className={compact ? 'px-4 py-6' : undefined}
      />
    );
  }

  const montantGroupe = (rows) => rows.reduce((somme, ligne) => somme + (Number(ligne.amount) || 0), 0);
  // Le total d'un mois est celui du serveur (tout le filtre); sans lui, la somme des lignes lues.
  const montantMois = (groupe) => {
    const duServeur = (totaux?.by_month || []).find((m) => (m.month || 'sans-date') === groupe.cle);
    return duServeur ? duServeur.amount : montantGroupe(groupe.lignes);
  };
  // Colonnes avant « Montant » : date, catégorie, détail, payé par, supporté par + celles du contexte.
  const avantMontant = 5 + (truckId ? 0 : 1) + (tripId ? 0 : 1) + (convoyId ? 0 : 1);

  if (compact) {
    const quiPaie = (ligne) =>
      ligne.kind === 'DRIVER_EXPENSE'
        ? `${texteSur(ligne.party_name, 'Chauffeur')} (avance)`
        : ligne.paid_by_owner
          ? 'le propriétaire'
          : texteSur(ligne.account_name, 'Caisse à préciser');
    return (
      <>
        <ul
          className={cn(
            'divide-y divide-border overflow-hidden rounded-xl border border-border bg-card',
            chargement && 'opacity-60 transition-opacity',
          )}
          aria-label="Frais"
          aria-busy={chargement}
          data-testid="expenses-table"
        >
          {groupes.map((groupe) => (
            <React.Fragment key={groupe.cle}>
              {groupe.titre && (
                <li className="flex items-baseline justify-between gap-3 bg-muted/40 px-3 py-2 text-sm font-semibold">
                  <span className="min-w-0 capitalize">{groupe.titre}</span>
                  <span className="shrink-0 tabular-nums">{formatCurrency(montantMois(groupe))}</span>
                </li>
              )}
              {groupe.lignes.map((ligne) => {
                const repere = [
                  formatDate(ligne.occurred_at),
                  !truckId && ligne.truck_plate,
                  !tripId && ligne.trip_ref && referenceLisible(ligne.trip_ref, ''),
                  !convoyId && ligne.convoy_ref && referenceLisible(ligne.convoy_ref, ''),
                  texteSur(ligne.reason || ligne.expense_type_label || ligne.notes, ''),
                ].filter(Boolean);
                return (
                  <li key={ligne.id} className="space-y-0.5 px-3 py-2.5" data-testid={`frais-${ligne.id}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="min-w-0 break-words text-sm font-medium">
                        {libelle('categorie_frais', ligne.category, 'Non classé')}
                      </span>
                      <span className="shrink-0 text-sm font-semibold tabular-nums">{formatCurrency(ligne.amount)}</span>
                    </div>
                    <p className="break-words text-xs text-muted-foreground">{repere.join(' · ')}</p>
                    <p className="break-words text-xs text-muted-foreground">
                      {`Payé par ${quiPaie(ligne)} · Supporté par ${libelle('porteur_frais', ligne.cost_bearer || 'SOCIETE')}${
                        ligne.recharged ? ' (refacturé)' : ''
                      }`}
                    </p>
                    {onCancel && (
                      <div className="pt-1">
                        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => onCancel(ligne)}>
                          Annuler
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </React.Fragment>
          ))}
          <li className="flex items-baseline justify-between gap-3 bg-muted/40 px-3 py-2.5 text-sm font-semibold">
            <span className="min-w-0">Total ({totaux?.count ?? lignes.length} frais)</span>
            <span className="shrink-0 tabular-nums">{formatCurrency(totaux?.amount ?? montantGroupe(lignes))}</span>
          </li>
        </ul>
        <BarrePagination pagination={pagination} nom="frais" />
      </>
    );
  }

  return (
    <>
      <div
        className={cn('overflow-hidden rounded-xl border border-border bg-card', chargement && 'opacity-60 transition-opacity')}
        aria-busy={chargement}
        data-testid="expenses-table"
      >
        <Table stickyFirstColumn={false}>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead>Détail</TableHead>
              {!truckId && <TableHead>Camion</TableHead>}
              {!tripId && <TableHead>Trajet</TableHead>}
              {!convoyId && <TableHead>Convoi</TableHead>}
              <TableHead>Payé par</TableHead>
              <TableHead>Supporté par</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              {onCancel && <TableHead className="w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupes.map((groupe) => (
              <React.Fragment key={groupe.cle}>
                {groupe.titre && (
                  <TableRow className="bg-muted/40">
                    <TableCell colSpan={avantMontant} className="font-semibold capitalize">
                      {groupe.titre}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatCurrency(montantMois(groupe))}
                    </TableCell>
                    {onCancel && <TableCell />}
                  </TableRow>
                )}
                {groupe.lignes.map((ligne) => (
                  <TableRow key={ligne.id} data-testid={`frais-${ligne.id}`}>
                    <TableCell className="whitespace-nowrap">{formatDate(ligne.occurred_at)}</TableCell>
                    <TableCell>{libelle('categorie_frais', ligne.category, 'Non classé')}</TableCell>
                    <TableCell className="max-w-[16rem] truncate">
                      {texteSur(ligne.reason || ligne.expense_type_label || ligne.notes, '—')}
                    </TableCell>
                    {!truckId && <TableCell className="whitespace-nowrap">{ligne.truck_plate || '—'}</TableCell>}
                    {!tripId && <TableCell>{ligne.trip_ref ? referenceLisible(ligne.trip_ref, '—') : '—'}</TableCell>}
                    {!convoyId && (
                      <TableCell className="whitespace-nowrap">{ligne.convoy_ref ? referenceLisible(ligne.convoy_ref, '—') : '—'}</TableCell>
                    )}
                    <TableCell>
                      {ligne.kind === 'DRIVER_EXPENSE'
                        ? `${texteSur(ligne.party_name, 'Chauffeur')} (avance)`
                        : ligne.paid_by_owner
                          ? 'Le propriétaire'
                          : texteSur(ligne.account_name, 'Caisse à préciser')}
                    </TableCell>
                    <TableCell>
                      {libelle('porteur_frais', ligne.cost_bearer || 'SOCIETE')}
                      {ligne.recharged ? ' · refacturé' : ''}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{formatCurrency(ligne.amount)}</TableCell>
                    {onCancel && (
                      <TableCell className="text-right">
                        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => onCancel(ligne)}>
                          Annuler
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={avantMontant} className="font-semibold">
                Total ({totaux?.count ?? lignes.length} frais)
              </TableCell>
              <TableCell className="text-right font-semibold tabular-nums">
                {formatCurrency(totaux?.amount ?? montantGroupe(lignes))}
              </TableCell>
              {onCancel && <TableCell />}
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <BarrePagination pagination={pagination} nom="frais" />
    </>
  );
}
